import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';

import { getAsset } from '../../../../../../util/assets';
import Button from '../../../../Button';
import PriceDisplay from '../../../../PriceDisplay';
import Badge from '../../../../Badge';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';

const propTypes = {
  className: PropTypes.string,
  printProduct: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    preview_asset_image: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    sizes: PropTypes.shape({
      total: PropTypes.number.isRequired,
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
  }).isRequired,
  onPrintProductSelect: PropTypes.func.isRequired,
  onPrintProductSizeSelected: PropTypes.func.isRequired,
  getPrintProductSizePrice: PropTypes.func,
  disabled: PropTypes.bool,
  disabledElement: PropTypes.node,
  hasIndividualSizeCartItem: PropTypes.bool,
};

const PrintProductGroup = ({
  className,
  printProduct,
  onPrintProductSelect,
  onPrintProductSizeSelected,
  getPrintProductSizePrice,
  disabled,
  disabledElement,
  hasIndividualSizeCartItem,
}) => {
  const openAlert = useOpenAlert();
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-print-product': true,
  });

  const previewImage = printProduct.preview_asset_image?.url;
  const isSingleSize = printProduct.sizes?.total === 1;
  const singleProductPrice = isSingleSize
    ? getPrintProductSizePrice?.(printProduct, printProduct.sizes.records[0]) ||
      printProduct.sizes.records[0].price
    : undefined;

  return (
    <Fragment>
      <div
        className={classes}
        data-testid={`PrintProductGroup-${printProduct.id}`}
      >
        <div className="purchasing-option-item-thumb">
          <img
            width={previewImage ? 80 : 60}
            src={previewImage || getAsset('/simple-prints-illustration.svg')}
            alt=""
          />
        </div>

        <div className="purchasing-option-item-content-wrap">
          <span className="purchasing-option-item-title">
            {printProduct.name}
          </span>
          <div className="purchasing-option-item-info-extra">
            {isSingleSize && singleProductPrice ? (
              <Badge badgeStyle="yellow" className="text-extra-bold">
                <PriceDisplay
                  price={singleProductPrice.value}
                  currency={singleProductPrice.currency}
                />
              </Badge>
            ) : (
              <span className="text-primary text-sm text-extra-bold">
                {printProduct?.sizes?.total}{' '}
                {pluralize('product', printProduct?.sizes?.total)}
              </span>
            )}
          </div>

          {disabled && disabledElement && <>{disabledElement}</>}

          <Button
            className={classNames({
              'purchasing-options-item-btn': true,
              'btn-purchase-options-in-cart': hasIndividualSizeCartItem,
              'mt-2': true,
            })}
            btnStyle={classNames({
              primary: !hasIndividualSizeCartItem,
              info: hasIndividualSizeCartItem,
            })}
            data-testid="btn-print-product-shop"
            onClick={() => {
              if (disabled) {
                openAlert({
                  type: 'info',
                  dismissible: false,
                  message: ({ onRequestClose }) => (
                    <div className="d-flex flex-column align-items-center p-2">
                      <img
                        src={getAsset('/icon-package-3d.svg')}
                        alt=""
                        className="mb-2"
                      />
                      <h4 className="text-center mb-3">
                        {printProduct.name} can only be purchased after adding a
                        print package to your cart.
                      </h4>
                      <Button onClick={onRequestClose}>OK</Button>
                    </div>
                  ),
                });
                return;
              }
              if (isSingleSize) {
                onPrintProductSizeSelected(printProduct.sizes?.records[0]);
              } else {
                onPrintProductSelect(printProduct);
              }
            }}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {isSingleSize
              ? hasIndividualSizeCartItem
                ? 'In cart'
                : 'Add to cart'
              : 'Shop'}
          </Button>
        </div>
      </div>
    </Fragment>
  );
};

PrintProductGroup.propTypes = propTypes;

export default PrintProductGroup;
